import { withAITracking } from '@microsoft/applicationinsights-react-js';
import Edit from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Box from '@mui/material/Box';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { StatesAndProvinces, useProductCenterApi } from 'api';
import { unitInfoQuery, unitStructureQuery } from 'api/queries';
import { AreaName } from 'domain/constants';
import { isRelocateEnabled } from 'domain/unit/Unit';
import { LocationUpdateEventType } from 'domain/unit/enums/LocationUpdateEventType';
import { SectionButton } from 'library/components/controls/buttons/SectionButton';
import { TextDisplay } from 'library/components/controls/display/TextDisplay';
import { SectionTitleButtons } from 'library/components/controls/layout/SectionTitleButtons';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';
import { OperationType } from 'library/models/enums/OperationType';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { reactPlugin } from 'main/azureInsights/config';
import { unitRelocateToPageUrl } from 'main/routes/urls/RouteUrls';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';
import { LocationCountryText } from 'modules/Shared/Components/OperatingLocation/LocationCountryText';
import { LocationStateText } from 'modules/Shared/Components/OperatingLocation/LocationStateText';

import EditOperatingLocationPage from './EditOperatingLocationPage';

const OperatingLocationSectionPage = (): React.JSX.Element => {
	const unitIds = useResolvedUnitIds();
	const { t } = useTranslation([
		'common',
		'commonUnit',
		'unitOverview',
		'productInOperation',
		'maintenancePlanner',
	]);
	const [unitLocationEdit, setUnitLocationEdit] = useState<boolean>(false);

	const { api } = useProductCenterApi();

	const queryStructure = useSuspenseQuery(unitStructureQuery(unitIds.id));

	const { data: unitData } = useSuspenseQuery(unitInfoQuery(api, unitIds.id));

	const showRelocationDate =
		unitData.currentLocation?.updateEvent ===
			LocationUpdateEventType.Transfer &&
		!!unitData.currentLocation.utcUpdateDate;

	if (unitLocationEdit) {
		return (
			<EditOperatingLocationPage
				currentLocation={unitData.currentLocation}
				unitId={unitIds.id}
				handleCancel={setUnitLocationEdit}
			/>
		);
	}

	return (
		<>
			<SectionTitleButtons>
				{isRelocateEnabled(queryStructure.data) && (
					<AuthorizeComponent
						areaName={AreaName.TransferUnit}
						permission={OperationType.Create}>
						<SectionButton
							text={t('commonUnit:button-relocate-to')}
							icon={<LocationOnIcon />}
							isLink
							to={generatePath(unitRelocateToPageUrl, {
								unitId: unitIds.publicId,
							})}
						/>
					</AuthorizeComponent>
				)}
				<AuthorizeComponent
					areaName={AreaName.Unit}
					permission={OperationType.Write}>
					<SectionButton
						text={t('unitOverview:edit-unit-location-title')}
						icon={<Edit />}
						onClick={() => setUnitLocationEdit(true)}
					/>
				</AuthorizeComponent>
			</SectionTitleButtons>
			<Box
				className="operating-location-view-section"
				sx={{
					display: 'grid',

					gridTemplateColumns: {
						xs: 'repeat(1, 1fr)',
						sm: 'repeat(2, 1fr)',
						md: 'repeat(3, 1fr)',
					},

					gap: 2,
					p: 2,
				}}>
				<TextDisplay
					label={t('common:country')}
					value={
						<LocationCountryText
							countryCode={unitData.currentLocation?.country}
						/>
					}
				/>
				<TextDisplay
					label={t('common:county')}
					value={
						<LocationStateText
							state={unitData.currentLocation?.state}
							countryCodeWithStatesAndProvinces={
								unitData.currentLocation
									?.country as StatesAndProvinces
							}
						/>
					}
				/>
				<TextDisplay
					label={t('common:city')}
					value={unitData.currentLocation?.city}
				/>
				<Box sx={{ gridColumn: { sm: 'span 3' } }}>
					<TextDisplay
						label={t('common:machine-location')}
						value={unitData.currentLocation?.machineVesselLocation}
					/>
				</Box>
				<TextDisplay
					label={t('common:zip-code')}
					value={unitData.currentLocation?.zipCode}
				/>
				<Box
					sx={{
						gridColumn: {
							sm: showRelocationDate ? 'span 1' : 'span 2',
						},
					}}>
					<TextDisplay
						label={t('common:address')}
						value={unitData.currentLocation?.address}
					/>
				</Box>
				{showRelocationDate && (
					<TextDisplay
						label={t('maintenancePlanner:relocation-date')}
						value={getLocalizedDateFormat(
							new Date(unitData.currentLocation!.utcUpdateDate!)
						)}
					/>
				)}
			</Box>
		</>
	);
};

export default withAITracking(
	reactPlugin,
	OperatingLocationSectionPage,
	'UnitOverviewOperatingLocationSectionPage',
	'application-insight-container'
);
